import { Button } from "@similarweb/ui-components";
import * as actions from "app/actions/educationBarActions";
import getUserInfo from "app/components/ContactUs/getContactUsUserInfo";
import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { allTrackers } from "app/services/track/track";
import { ArticlesService } from "./ArticlesService";
import { i18nFilter } from "app/filters/i18nFilter";
import { withUIRouter } from "app/components/React/HOCs/withUIRouter";
import type { UIRouterReact } from "@uirouter/react/lib/core";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ArticleType } from "@similarweb/pro-education-bar";
import { EducationBar } from "@similarweb/pro-education-bar";
import { CookieManager } from "app/components/cookie-manager/CookieManager";

class EducationBarContainer extends React.Component<
    {
        router: UIRouterReact;
        toggleEducationBar: (state: boolean) => void;
        isSidebarOpen: boolean;
        article: any;
        navOverrideMode: string;
    },
    any
> {
    private messages: any;
    private swNavigator: any;
    private swNavigatorState: any;
    private navigationStateChangeListener;
    private track;
    private userInfo;
    private i18n;
    private locale: string;

    constructor(props) {
        super(props);

        this.state = {
            educationBarContext: {
                articlesFilter: "",
            },
        };

        this.i18n = i18nFilter();
        this.swNavigator = proNavigator;
        this.swNavigatorState = this.swNavigator.current();
        this.userInfo = getUserInfo();

        const cookieManager: CookieManager = new CookieManager();
        this.locale = cookieManager.getCookie("locale") || "en-us";

        this.track = (category, action, name) => {
            allTrackers.trackEvent(category, action, name);
        };

        this.messages = this.setMessage();

        this.sideBarToggleHandler = this.sideBarToggleHandler.bind(this);
        this.closeButtonClickHandler = this.closeButtonClickHandler.bind(this);
        this.supportLinkClickHandler = this.supportLinkClickHandler.bind(this);
        this.searchSubmitHandler = this.searchSubmitHandler.bind(this);
        this.articleTitleClickHandler = this.articleTitleClickHandler.bind(this);
        this.zendeskLinkOpenHandler = this.zendeskLinkOpenHandler.bind(this);
        this.backToDefaultClickHandler = this.backToDefaultClickHandler.bind(this);
        this.backToArticlesClickHandler = this.backToArticlesClickHandler.bind(this);
        this.articleAttachmentClick = this.articleAttachmentClick.bind(this);
        this.handleSupportButtonClick = this.handleSupportButtonClick.bind(this);
    }

    public componentDidMount() {
        this.navigationStateChangeListener = this.props.router.transitionService.onSuccess({}, (tranaition) => {
            this.props.toggleEducationBar(false);
        });
    }

    public componentWillUnmount(): void {
        if (typeof this.navigationStateChangeListener === "function") {
            this.navigationStateChangeListener();
        }
    }

    public render() {
        return (
            <EducationBar
                messages={this.messages}
                isSidebarOpen={this.props.isSidebarOpen}
                article={this.props.article}
                fetchArticles={ArticlesService.GetArticlesByFilter}
                fetchAttachments={ArticlesService.GetAttachments}
                onArticleViewed={ArticlesService.OnArticleViewed}
                educationBarContext={this.state.educationBarContext}
                onSidebarToggle={this.sideBarToggleHandler}
                onCloseButtonClick={this.closeButtonClickHandler}
                onSupportLinkClick={this.supportLinkClickHandler}
                onSearchSubmit={this.searchSubmitHandler}
                onArticleTitleClick={this.articleTitleClickHandler}
                onZendeskLinkOpen={this.zendeskLinkOpenHandler}
                onBackToDefaultClick={this.backToDefaultClickHandler}
                onBackToArticlesClick={this.backToArticlesClickHandler}
                onArticleAttachmentClick={this.articleAttachmentClick}
                renderContactUs={() => (
                    <Button
                        type="upsell"
                        onClick={this.handleSupportButtonClick}
                    >
                        {this.messages.contactButtonText}
                    </Button>
                )}
            />
        );
    }

    public getSearchTag(newState: any): string {
        const state = newState || this.swNavigator.current();

        if (!state.data) {
            const { pageId } = state;
            if (!pageId) {
                return undefined;
            }
            return _.filter([pageId.section, pageId.subSection, pageId.subSubSection]).join("_").toUpperCase();
        }
        const searchTag: any = state.data.educationBarTag;
        switch (typeof searchTag) {
            case "string":
                return searchTag.toString();
            case "function":
                return searchTag(state);
        }
        return undefined;
    }

    public getArticlesTitle(newState: any): string {
        const state = newState || this.swNavigator.current();
        return this.i18n(state.pageTitle);
    }

    private setArticlesAndTitle = () => {
        const tag = this.getSearchTag(this.swNavigator.current());
        this.messages.articlesListTitle = this.getArticlesTitle(this.swNavigator.current());

        this.setState({
            educationBarContext: {
                articlesFilter: tag,
            },
        });
    };

    private sideBarToggleHandler(isOpen: boolean): void {
        allTrackers.dispatchTrackEvent(
            "Side Menu",
            "click",
            `${this.props.navOverrideMode || ""}/Main Sidebar/education and help/${isOpen ? "open" : "close"}`,
        );
        if (isOpen) {
            this.setArticlesAndTitle();
        }
    }

    private setMessage(): object {
        return {
            articlesListTitle: this.i18n(this.swNavigatorState.pageTitle), // add default props like default messaging
            educationBarTitle: this.i18n("educationBar.educationBarTitle"),
            contactButtonText: this.i18n("educationBar.contactButtonText"),
            searchBarPlaceholder: this.i18n("educationBar.searchBarPlaceholder"),
            supportLinkText: this.i18n("educationBar.supportLinkText"),
            emptyStateText: this.i18n("educationBar.emptyStateText"),
            emptyStateDescription: this.i18n("educationBar.emptyStateDescription"),
        };
    }

    private getArticleType(articleType: number): string {
        return articleType === 1 ? "Document" : "Video";
    }

    private supportHomepageUrl = `https://support.similarweb.com/`;

    private handleSupportButtonClick = () => {
        // Open a new tab and set its URL
        window.open(this.supportHomepageUrl, "_blank", "noopener,noreferrer");
        allTrackers.dispatchTrackEvent("Side Menu", "click", "contact support");
    };

    private closeButtonClickHandler(): void {
        this.track("side bar", "close", "education bar");
        this.props.toggleEducationBar(false);
    }

    private supportLinkClickHandler(): void {
        this.track("internal link", "click", "education bar/support");
    }

    private searchSubmitHandler(searchText: string): void {
        if (searchText) {
            this.track("Search Bar", "click", `education bar/${searchText}`);
        }
    }

    private articleTitleClickHandler(articleType: ArticleType, articleTitle: string): void {
        this.track("side bar", "open", `education bar/${this.getArticleType(articleType)}/${articleTitle}`);
    }

    private zendeskLinkOpenHandler(articleType: ArticleType, articleTitle: string): void {
        this.track("internal link", "click", `education bar/${this.getArticleType(articleType)}/${articleTitle}`);
    }

    private backToDefaultClickHandler(): void {
        this.track("side bar", "back", "education bar");
    }

    private backToArticlesClickHandler(articleType: ArticleType, articleTitle: string): void {
        this.track("side bar", "back", `education bar/${this.getArticleType(articleType)}/${articleTitle}`);
    }

    private articleAttachmentClick(articleTitle: string, attachmentName: string): void {
        this.track("Download", "submit-ok", `education bar/${articleTitle}/${attachmentName}`);
    }
}

function mapDispatchToProps(dispatch): object {
    return {
        toggleEducationBar: (isOpen) => {
            dispatch(actions.toggleEducationBar(isOpen));
        },
    };
}

function mapStateToProps(state): object {
    return {
        isSidebarOpen: state.educationBar.isOpen,
        article: state.educationBar.article,
        navOverrideMode: state.primaryNavOverride.navOverrideMode,
    };
}

const component = connect(mapStateToProps, mapDispatchToProps)(withUIRouter(EducationBarContainer));

export default component;
